import React, {FunctionComponent, ReactNode, useMemo} from 'react';
import {Box, InputAdornment, TextField, useTheme, type SxProps, type Theme} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {useCommandSearchShortcutLabel} from '../engine/use-command-shortcut';
import {useHasKeyboardInput} from '../engine/use-has-keyboard-input';

interface CommandSearchTriggerProps {
    onOpen: () => void;
    placeholder?: string;
    /** Replaces the default trailing search icon when provided (e.g. search-mode toggler). */
    endAdornment?: ReactNode;
    fullWidth?: boolean;
    sx?: SxProps<Theme>;
}

const ShortcutKey: FunctionComponent<{ label: string }> = ({label}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            px: 0.5,
            py: 0.125,
            borderRadius: 0.75,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontSize: '0.65rem',
            fontWeight: 700,
            lineHeight: 1.2,
            color: 'text.secondary',
        }}
    >
        {label}
    </Box>
);

/** App bar search trigger that opens the command palette. */
const CommandSearchTrigger: FunctionComponent<CommandSearchTriggerProps> = ({
    onOpen,
    placeholder = 'Search this panel…',
    endAdornment,
    fullWidth = false,
    sx,
}) => {
    const theme = useTheme();
    const shortcutLabel = useCommandSearchShortcutLabel();
    const hasKeyboardInput = useHasKeyboardInput();

    const [modifierKey, actionKey] = useMemo(() => {
        if (shortcutLabel.includes('⌘')) {
            return ['⌘', 'K'];
        }
        return ['Ctrl', 'K'];
    }, [shortcutLabel]);

    const defaultEndAdornment = hasKeyboardInput ? (
        <InputAdornment position="end">
            <SearchRounded fontSize="small" color="action"/>
        </InputAdornment>
    ) : undefined;

    return (
        <TextField
            value=""
            placeholder={placeholder}
            onClick={onOpen}
            onKeyDown={(event) => {
                // Never react to modifier combos (⌘K is handled globally) or modifier-only keys.
                if (event.metaKey || event.ctrlKey || event.altKey) {
                    return;
                }
                const modifierOnlyKeys = ['Meta', 'Control', 'Alt', 'Shift', 'Tab'];
                if (modifierOnlyKeys.includes(event.key)) {
                    return;
                }
                // Open on Enter, Space, or a single printable character.
                const isActivationKey = event.key === 'Enter' || event.key === ' ';
                const isPrintableChar = event.key.length === 1;
                if (!isActivationKey && !isPrintableChar) {
                    return;
                }
                event.preventDefault();
                onOpen();
            }}
            variant="outlined"
            size="small"
            fullWidth={fullWidth}
            sx={{
                ...(fullWidth
                    ? {}
                    : {
                        minWidth: {xs: 180, sm: 200, md: 320},
                        maxWidth: 420,
                    }),
                backgroundColor: 'background.paper',
                borderRadius: `${theme.shape.borderRadius}px`,
                cursor: 'pointer',
                '& fieldset': {border: 'none'},
                '& .MuiInputBase-input': {
                    cursor: 'pointer',
                    fontSize: theme.typography.subtitle2.fontSize,
                },
                ...sx,
            }}
            InputProps={{
                readOnly: true,
                startAdornment: (
                    <InputAdornment position="start">
                        {hasKeyboardInput ? (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5}}>
                                <ShortcutKey label={modifierKey}/>
                                <ShortcutKey label={actionKey}/>
                            </Box>
                        ) : (
                            <SearchRounded fontSize="small" color="action" sx={{mr: 0.5}}/>
                        )}
                    </InputAdornment>
                ),
                endAdornment: endAdornment != null ? endAdornment : defaultEndAdornment,
            }}
        />
    );
};

export default CommandSearchTrigger;
